import { fetchApi, getFetchApiJSONResponse, catchFetchApiError } from 'shared/functions';
import { useGet } from 'shared/hooks/useFetch';
import {
  CoverageOptions,
  FilterOptions,
  ImpactReview,
  IndustryScore,
  List,
  MaterialityMap,
  PortfolioCoverage,
  RegionScore,
  RelevanceMapping,
  ScoreOverview,
} from '../types';

export type Metadata = { latestUpdate: string; year: number };

export const getPieByRegion = async (id: string, meta: Metadata) => {
  return fetchApi(
    `/esg/api/${id}/dashboard/lending-portfolio-by-region?latestUpdate=${meta.latestUpdate}&year=${meta.year}`,
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const getPieByIndustry = async (id: string, meta: Metadata) => {
  return fetchApi(
    `/esg/api/${id}/dashboard/lending-portfolio-by-industry?latestUpdate=${meta.latestUpdate}&year=${meta.year}`,
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const getImpactByCountry = async (id: string, meta: Metadata) => {
  return fetchApi(
    `/esg/api/${id}/dashboard/impact-of-lending-portfolio-by-country?latestUpdate=${meta.latestUpdate}&year=${meta.year}`,
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const getImpactByIndustry = async (id: string, meta: Metadata) => {
  return fetchApi(
    `/esg/api/${id}/dashboard/impact-of-lending-portfolio-by-industry?latestUpdate=${meta.latestUpdate}&year=${meta.year}`,
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const getEsgCoverage = (query: CoverageOptions) => {
  const {
    country,
    industry,
    limit,
    offset,
    orderBy,
    environmentFrom,
    environmentTo,
    socialFrom,
    socialTo,
    governanceFrom,
    governanceTo,
    totalScoreFrom,
    totalScoreTo,
  } = query;

  return fetchApi(
    `/esg/api/esg-coverage${
      Object.values(query).some((val) => val != null)
        ? `?${[
            limit != null ? `limit=${limit}` : '',
            offset != null ? `offset=${offset}` : '',
            country ? country.map((code) => `country=${code}`) : '',
            industry ? industry.map((code) => `industry=${code}`) : '',
            orderBy ? `order_by=${orderBy}` : '',
            environmentFrom ? `e_score_min=${environmentFrom}` : '',
            environmentTo ? `e_score_max=${environmentTo}` : '',
            socialFrom ? `s_score_min=${socialFrom}` : '',
            socialTo ? `s_score_max=${socialTo}` : '',
            governanceFrom ? `g_score_min=${governanceFrom}` : '',
            governanceTo ? `g_score_max=${governanceTo}` : '',
            totalScoreFrom ? `esg_score_min=${totalScoreFrom}` : '',
            totalScoreTo ? `esg_score_max=${totalScoreTo}` : '',
          ]
            .flat()
            .filter(Boolean)
            .join('&')}`
        : ``
    }`,
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const useCompanyVsValueChain = (id: string, year: number) =>
  useGet<MaterialityMap>(`/esg/api/company-vs-value-chain/${id}/`, { params: { year } });

export const useGetImpactIndustryAbsolute = (id: string, year: number) =>
  useGet<List<IndustryScore>>(`/esg/api/impact-industry-absolute/`, {
    params: { company_uuid: id, year },
  });

export const useGetImpactIndustryRelative = (id: string, year: number) =>
  useGet<List<IndustryScore>>(`/esg/api/impact-industry-relative/`, {
    params: { company_uuid: id, year },
  });

export const useGetImpactRegionAbsolute = (id: string, year: number) =>
  useGet<List<RegionScore>>(`/esg/api/impact-region-absolute/`, {
    params: { company_uuid: id, year },
  });

export const useGetImpactRegionRelative = (id: string, year: number) =>
  useGet<List<RegionScore>>(`/esg/api/impact-region-relative/`, {
    params: { company_uuid: id, year },
  });

export const useMaterialityMap = (id: string, year: number) =>
  useGet<MaterialityMap>(`/esg/api/materiality-map/${id}/`, { params: { year } });

export const useGetScoreOverview = (id: string, year: number) =>
  useGet<ScoreOverview>(`/esg/api/score-overview/${id}/`, { params: { year } });

export const useSDGRelevanceMapping = (id: string, year: number) =>
  useGet<RelevanceMapping>(`/esg/api/sdg-relevance-mapping/${id}/`, { params: { year } });

export const useImpactReview = (id: string, year?: number) =>
  useGet<ImpactReview>(`/esg/api/impact-review/${id}/`, { params: { year } });

export const usePortfolioCoverage = (id: string, year: number) =>
  useGet<PortfolioCoverage>(`/esg/api/portfolio-coverage/${id}/`, { params: { year } });

export const useFilterOptions = () => useGet<FilterOptions>('/esg/api/esg-filter-options');
